import dayjs from 'dayjs';
import { verifyDateStringOrThrow } from './datetime';

export type HealthInsightPeriod = 'week' | 'month';

export type HealthInsightDomainKey = 'calories' | 'nutrition' | 'weight' | 'inbody' | 'activity' | 'water';

export type HealthInsightKind = 'good' | 'neutral' | 'bad';

export type HealthInsightTrend = 'up' | 'down' | 'flat';

export type HealthPeriodBounds = {
    period: HealthInsightPeriod;
    periodKey: string;
    fromDateKey: string;
    toDateKey: string;
    label: string;
    dayLabels: string[];
};

const WEEKDAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

function pad2(n: number): string {
    return String(n).padStart(2, '0');
}

function mondayOf(day: dayjs.Dayjs): dayjs.Dayjs {
    const wd = day.day();
    const offset = wd === 0 ? 6 : wd - 1;
    return day.subtract(offset, 'day').startOf('day');
}

export function isoWeekPeriodKey(fromMonday: dayjs.Dayjs): string {
    const thursday = fromMonday.add(3, 'day');
    const yearStart = dayjs(`${thursday.year()}-01-01`);
    const yearMonday = mondayOf(yearStart);
    const weekNum = Math.floor(fromMonday.diff(yearMonday, 'day') / 7) + 1;
    return `${thursday.year()}-W${pad2(weekNum)}`;
}

export function resolveHealthInsightPeriodBounds(
    period: HealthInsightPeriod,
    periodKey: string | null | undefined,
    anchorCalendarDay: string,
): HealthPeriodBounds {
    verifyDateStringOrThrow(anchorCalendarDay);
    const anchor = dayjs(anchorCalendarDay);

    if (period === 'week') {
        let monday: dayjs.Dayjs;
        if (periodKey?.match(/^\d{4}-W\d{2}$/)) {
            const [ys, ws] = periodKey.split('-W');
            const year = Number(ys);
            const week = Number(ws);
            const jan4 = dayjs(`${year}-01-04`);
            monday = mondayOf(jan4).add(week - 1, 'week');
        } else {
            monday = mondayOf(anchor);
        }
        const sunday = monday.add(6, 'day');
        const key = isoWeekPeriodKey(monday);
        return {
            period: 'week',
            periodKey: key,
            fromDateKey: monday.format('YYYY-MM-DD'),
            toDateKey: sunday.format('YYYY-MM-DD'),
            label: `${monday.format('MMM D')} – ${sunday.format('MMM D')}`,
            dayLabels: [...WEEKDAY_SHORT],
        };
    }

    let monthStart: dayjs.Dayjs;
    if (periodKey?.match(/^\d{4}-\d{2}$/)) {
        monthStart = dayjs(`${periodKey}-01`);
    } else {
        monthStart = anchor.startOf('month');
    }
    const monthEnd = monthStart.endOf('month');
    const daysInMonth = monthEnd.date();
    const weekCount = Math.ceil(daysInMonth / 7);
    const dayLabels = Array.from({ length: weekCount }, (_, i) => `W${i + 1}`);
    return {
        period: 'month',
        periodKey: monthStart.format('YYYY-MM'),
        fromDateKey: monthStart.format('YYYY-MM-DD'),
        toDateKey: monthEnd.format('YYYY-MM-DD'),
        label: monthStart.format('MMMM'),
        dayLabels,
    };
}

export function priorHealthInsightPeriodKey(period: HealthInsightPeriod, anchorCalendarDay: string): string {
    verifyDateStringOrThrow(anchorCalendarDay);
    const anchor = dayjs(anchorCalendarDay);
    if (period === 'week') {
        const priorMonday = mondayOf(anchor).subtract(1, 'week');
        return isoWeekPeriodKey(priorMonday);
    }
    return anchor.subtract(1, 'month').format('YYYY-MM');
}

export function currentHealthInsightPeriodKey(period: HealthInsightPeriod, anchorCalendarDay: string): string {
    return resolveHealthInsightPeriodBounds(period, null, anchorCalendarDay).periodKey;
}

export function scoreToGrade(score: number): string {
    if (score >= 97) return 'A+';
    if (score >= 93) return 'A';
    if (score >= 90) return 'A-';
    if (score >= 87) return 'B+';
    if (score >= 83) return 'B';
    if (score >= 80) return 'B-';
    if (score >= 77) return 'C+';
    if (score >= 73) return 'C';
    if (score >= 70) return 'C-';
    if (score >= 67) return 'D+';
    if (score >= 63) return 'D';
    if (score >= 60) return 'D-';
    return 'F';
}

export function kindFromScore(score: number): HealthInsightKind {
    if (score >= 75) return 'good';
    if (score >= 60) return 'neutral';
    return 'bad';
}

export function trendFromDelta(delta: number, epsilon = 1): HealthInsightTrend {
    if (delta > epsilon) return 'up';
    if (delta < -epsilon) return 'down';
    return 'flat';
}

export function clampScore(n: number): number {
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, Math.min(100, Math.round(n)));
}

export function pctOfGoalScore(actual: number, goal: number, sweetSpot = 1): number {
    if (!goal || goal <= 0) return 50;
    const ratio = actual / goal;
    const err = Math.abs(ratio - sweetSpot);
    return clampScore(100 - err * 120);
}

export function underGoalBetterScore(actual: number, goal: number): number {
    if (!goal || goal <= 0) return 50;
    const ratio = actual / goal;
    if (ratio <= 1) return clampScore(100 - (1 - ratio) * 40);
    return clampScore(100 - (ratio - 1) * 100);
}

export function avg(nums: number[]): number {
    if (!nums.length) return 0;
    return nums.reduce((a, b) => a + b, 0) / nums.length;
}

export function sum(nums: number[]): number {
    return nums.reduce((a, b) => a + b, 0);
}

export function enumerateDateKeys(fromDateKey: string, toDateKey: string): string[] {
    verifyDateStringOrThrow(fromDateKey);
    verifyDateStringOrThrow(toDateKey);
    const out: string[] = [];
    let cur = dayjs(fromDateKey);
    const end = dayjs(toDateKey);
    while (cur.isBefore(end) || cur.isSame(end, 'day')) {
        out.push(cur.format('YYYY-MM-DD'));
        cur = cur.add(1, 'day');
    }
    return out;
}

export function bucketMonthWeekAverages(dateKeys: string[], valuesByDate: Map<string, number>, fromDateKey: string): number[] {
    const from = dayjs(fromDateKey);
    const buckets = new Map<number, number[]>();
    for (const key of dateKeys) {
        const v = valuesByDate.get(key);
        if (v == null) continue;
        const weekIdx = Math.floor(dayjs(key).diff(from, 'day') / 7);
        const list = buckets.get(weekIdx) ?? [];
        list.push(v);
        buckets.set(weekIdx, list);
    }
    const maxWeek = Math.max(0, ...buckets.keys());
    return Array.from({ length: maxWeek + 1 }, (_, i) => {
        const list = buckets.get(i) ?? [];
        return list.length ? avg(list) : 0;
    });
}

export const MIN_LOGGED_DAYS_FOR_INSIGHTS = 3;

export function isEligibleForInsights(loggedDays: number): boolean {
    return loggedDays >= MIN_LOGGED_DAYS_FOR_INSIGHTS;
}

export function healthInsightPeriodCrossed(period: HealthInsightPeriod, previousCalendarDay: string, nextCalendarDay: string): boolean {
    return currentHealthInsightPeriodKey(period, previousCalendarDay) !== currentHealthInsightPeriodKey(period, nextCalendarDay);
}
